import { useInjection } from "infrastructure/ioc/ioc.react";
import { HeatConsumptionType } from "../enums/heatConsumptionType";
import IRepository from "data/IRepository";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { HeatConsumptionDataResultDTO } from "../dtos/HeatConsumptionDataResultDTO";
import { useEffect, useState } from "react";
import DataQuery from "data/DataQuery";
import { Consumption_ConsumptionHeat } from "data/endpoints";
import { QuotationType } from "features/shared/enums/quotationType";
import { formatISO, lastDayOfMonth } from "date-fns";
import { trackPromise } from "react-promise-tracker";
import { DataLoadStatus } from "data/enums/dataLoadStatus";

type HeatConsumptionHookResult = {
  heatConsumptions: Record<string, HeatConsumptionDataResultDTO>;
  loadStatus: DataLoadStatus;
}

export const useHeatConsumptions = (
  contractId: string,
  types: HeatConsumptionType[],
  year: number,
  month: number
): HeatConsumptionHookResult => {
  // DI
  const heatConsumptionRepo = useInjection<
    IRepository<HeatConsumptionDataResultDTO>
  >(IocTypes.Repository);

  // state
  const [heatConsumptionsByTypes, setHeatConsumptionsByTypes] = useState<Record<string, HeatConsumptionDataResultDTO>>({});
  const [loadStatus, setLoadStatus] = useState(DataLoadStatus.BeforeFirstLoad); // indication if data was loaded for the first time

  // side effects
  // fetch consumptions by types
  useEffect(() => {
    if (types.length > 0) {
      try {
        trackPromise(fetchAndSetConsumptions());
      } catch (error) {
        setLoadStatus("LoadError");
      }
    }
  }, [contractId, types, year, month]);

  // helpers
  const fetchAndSetConsumptions = async () => { 
    let result: Record<string, HeatConsumptionDataResultDTO> = {};

    for (const heatType of types) {
      let conumptionsByType =  await fetchConsumptionsByType(heatType);
      result[heatType] = conumptionsByType;
    }

    setHeatConsumptionsByTypes(result);
    setLoadStatus("AfterFirstLoad");
  }

  const fetchConsumptionsByType = async (type: HeatConsumptionType) => {
    const queryParams = new URLSearchParams({
      ContractId: contractId,
      From: getDateStr("first"),
      To:  getDateStr("last"),
      HeatConsumptionType: type,
      QuotationType: QuotationType.Month
    });

    const query: DataQuery = {
      url: Consumption_ConsumptionHeat,
      params: queryParams,
    };

    return await heatConsumptionRepo.getOne(query);
  };

  const getDateStr = (dayType: "first" | "last") => { 
    let date = new Date(year, month - 1, 1);

    if (dayType == "last") {
      date = lastDayOfMonth(date);
    }

    return formatISO(date, { representation: "date" });
  }

  // return
  return { 
    heatConsumptions: heatConsumptionsByTypes,
    loadStatus: loadStatus
   };
};
